import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useKmpSearch from '@/hooks/useKmpSearch';
import { useLazyGetProvidersQuery } from '@/redux/api/client/creditTracker';
import { selectProviders } from '@/redux/selectors/creditTrackerSelectors';
import {
  creditTrackerFailure,
  creditTrackerProvidersSuccess
} from '@/redux/slices/creditTrackerSlice';

const useCleProviderSearch = () => {
  const dispatch = useDispatch();
  const { kmpSearch } = useKmpSearch();
  const allProviders = useSelector(selectProviders);
  const [getProviders, providerResult] = useLazyGetProvidersQuery();

  const minNumberOfCharacters = 3;

  useEffect(() => {
    providerResult?.isError && dispatch(creditTrackerFailure(providerResult?.error));
  }, [dispatch, providerResult?.error, providerResult?.isError]);

  useEffect(() => {
    if (providerResult.data) {
      dispatch(creditTrackerProvidersSuccess(providerResult.data));
    }
  }, [dispatch, providerResult.data]);

  const providers = useMemo(async () => {
    if (!allProviders.length) {
      const res = await getProviders(null, true);

      if (res.data) return [...res.data];
    }

    return allProviders;
  }, [allProviders, getProviders]);

  /** Search for matching CLE providers based on a search phrase. */
  const search = useCallback(
    async (searchPhrase: string): Promise<string[] | undefined> => {
      if (
        !searchPhrase?.length ||
        searchPhrase.length < minNumberOfCharacters ||
        searchPhrase?.trim() == ''
      )
        return;

      searchPhrase = searchPhrase.toLowerCase();

      return kmpSearch(searchPhrase, await providers);
    },
    [kmpSearch, providers]
  );

  return {
    search
  };
};
export default useCleProviderSearch;
